import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { Icon, IconSize, IconType, Popover } from "@dexibit/core";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { ProviderStatus, useGetProviderStatusesQuery } from "../../store/services/status";

import { matchesUrlPatterns } from "./helpers";
import type { BannerSeverity } from "./styles";
import {
  BannerWrapper,
  LearnMoreButton,
  MarqueeContainer,
  MarqueeItem,
  MarqueeTrack,
  PopoverContent,
  VerticalCycler,
  VerticalItem,
} from "./styles";

const PROVIDER_MESSAGES: Record<string, string> = {
  anthropic: "Ask may be temporarily unavailable due to a service disruption.",
  openai: "Magic Wand AI may be temporarily unavailable due to a service disruption.",
  motherduck:
    "Data querying across the application may be temporarily unavailable due to a service disruption.",
};

const CYCLE_INTERVAL_MS = 8000;
const MARQUEE_DURATION_PER_ITEM = 16;
const POLLING_INTERVAL = 2 * 60 * 1000;

const getMessage = (status: ProviderStatus) =>
  PROVIDER_MESSAGES[status.provider] ?? `${status.provider} is experiencing issues.`;

interface MessageContentProps {
  status: ProviderStatus;
  onLearnMoreClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const MessageContent = ({ status, onLearnMoreClick }: MessageContentProps) => (
  <>
    <Icon type={IconType.fa} icon={faTriangleExclamation} size={IconSize.xs} />
    <span>
      {getMessage(status)}
      {status.summary && onLearnMoreClick && (
        <LearnMoreButton onClick={onLearnMoreClick}>Learn more</LearnMoreButton>
      )}
    </span>
  </>
);

export const OutageBanner = () => {
  const { data: statuses } = useGetProviderStatusesQuery(undefined, {
    pollingInterval: POLLING_INTERVAL,
  });
  const { pathname } = useLocation();

  const [activeIndex, setActiveIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(null);
  const [popoverSummary, setPopoverSummary] = useState("");
  const popoverOpen = Boolean(popoverAnchor);

  const filteredStatuses = useMemo(
    () => statuses?.filter((s) => matchesUrlPatterns(pathname, s.urlPatterns)) ?? [],
    [statuses, pathname],
  );

  const count = filteredStatuses.length;
  // Stable key for the current set of visible providers — reset cycling when it changes
  const filteredKey = filteredStatuses.map((s) => s.provider).join(",");
  const hoveredRef = useRef(false);

  const handleBannerMouseEnter = useCallback(() => {
    hoveredRef.current = true;
  }, []);

  const handleBannerMouseLeave = useCallback(() => {
    hoveredRef.current = false;
  }, []);

  const handleLearnMoreClick = useCallback(
    (summary: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setPopoverAnchor(event.currentTarget);
      setPopoverSummary(summary);
    },
    [],
  );

  // Pre-compute click handlers so all instances (including duplicates) get the same props
  const learnMoreHandlers = useMemo(
    () =>
      new Map(
        filteredStatuses
          .filter((s) => s.summary)
          .map((s) => [s.provider, handleLearnMoreClick(s.summary)]),
      ),
    [filteredStatuses, handleLearnMoreClick],
  );

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  useEffect(() => {
    setAnimate(false);
    setActiveIndex(0);
    requestAnimationFrame(() => setAnimate(true));

    if (count <= 1) return;

    const interval = setInterval(() => {
      if (!hoveredRef.current) {
        setActiveIndex((prev) => prev + 1);
      }
    }, CYCLE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [filteredKey, count]);

  // Snap back to first item after animating past the duplicate
  useEffect(() => {
    if (activeIndex !== count || count <= 1) return;

    const timeout = setTimeout(() => {
      setAnimate(false);
      setActiveIndex(0);
      requestAnimationFrame(() => setAnimate(true));
    }, 400);

    return () => clearTimeout(timeout);
  }, [activeIndex, count]);

  if (!filteredStatuses.length) {
    return null;
  }

  const activeStatus = filteredStatuses[activeIndex % count];
  const severity: BannerSeverity =
    activeStatus?.status === "MAJOR_OUTAGE" ? "critical" : "warning";
  const worstSeverity: BannerSeverity = filteredStatuses.some(
    (s) => s.status === "MAJOR_OUTAGE",
  )
    ? "critical"
    : "warning";

  return (
    <>
      <BannerWrapper
        $severity={severity}
        onMouseEnter={handleBannerMouseEnter}
        onMouseLeave={handleBannerMouseLeave}
      >
        <VerticalCycler $offset={activeIndex} $animate={animate}>
          {filteredStatuses.map((status) => (
            <VerticalItem key={status.provider}>
              <MessageContent
                status={status}
                onLearnMoreClick={learnMoreHandlers.get(status.provider)}
              />
            </VerticalItem>
          ))}
          {count > 1 && filteredStatuses[0] && (
            <VerticalItem key={`${filteredStatuses[0].provider}-dup`}>
              <MessageContent
                status={filteredStatuses[0]}
                onLearnMoreClick={learnMoreHandlers.get(filteredStatuses[0].provider)}
              />
            </VerticalItem>
          )}
        </VerticalCycler>

        <MarqueeContainer $severity={worstSeverity}>
          <MarqueeTrack $count={count} $durationPerItem={MARQUEE_DURATION_PER_ITEM}>
            {filteredStatuses.map((status) => (
              <MarqueeItem key={status.provider}>
                <MessageContent
                  status={status}
                  onLearnMoreClick={learnMoreHandlers.get(status.provider)}
                />
              </MarqueeItem>
            ))}
            {filteredStatuses.map((status) => (
              <MarqueeItem key={`${status.provider}-dup`}>
                <MessageContent
                  status={status}
                  onLearnMoreClick={learnMoreHandlers.get(status.provider)}
                />
              </MarqueeItem>
            ))}
          </MarqueeTrack>
        </MarqueeContainer>
      </BannerWrapper>

      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        sx={{ mt: 1 }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <PopoverContent>{popoverSummary}</PopoverContent>
      </Popover>
    </>
  );
};
